<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { Constants } from '@/utilities/constants.ts';
import { useI18n } from 'vue-i18n';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/vue/20/solid';
import type { GetProjectDetailsProjectResponse } from '@/api';

const { project, modelId } = defineProps<{
  project: GetProjectDetailsProjectResponse;
  modelId: number;
}>();

const i18n = useI18n();
const { t } = i18n;

const modelPrice = computed(() => {
  const model = project.models.find((model) => model.id === modelId);
  if (model && model.price.type.id == Constants.PriceTypeId.ForSale && model.price.amount > 0) {
    return model.price.amount;
  }
  return undefined;
});

const _purchasePrice = ref<number>(500000);
const _downPayment = ref<number>(100000);
const _interestRate = ref<number>(5.5);

const purchasePrice = computed({
  get: () => _purchasePrice.value,
  set: (val: number) => {
    _purchasePrice.value = Math.max(0, val || 0);
  },
});
const downPayment = computed({
  get: () => _downPayment.value,
  set: (val: number) => {
    _downPayment.value = Math.max(0, Math.min(val || 0, purchasePrice.value));
  },
});
const interestRate = computed({
  get: () => _interestRate.value,
  set: (val: number) => {
    _interestRate.value = Math.max(0, val || 0);
  },
});

const amortizationYears = ref<string>('25');
const termYears = ref<string>('5');
const paymentFrequency = ref<string>('12');
const showLegal = ref<boolean>(false);

const loanAmount = computed(() => Math.max(0, purchasePrice.value - downPayment.value));

const periodicPayment = computed(() => {
  const frequency = parseInt(paymentFrequency.value);
  const years = parseInt(amortizationYears.value);

  const P = loanAmount.value;
  const r = interestRate.value / 100 / frequency;
  const n = years * frequency;

  if (r === 0) {
    return Math.max(0, P / n);
  }

  const payment = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);

  return Math.max(0, payment);
});

const mortgageAmount = computed(() => {
  const value = Math.max(0, loanAmount.value);
  const locale = i18n.locale.value;
  return value.formatCurrency(locale);
});

const paymentAmount = computed(() => {
  const value = Math.max(0, Math.round(periodicPayment.value));
  const locale = i18n.locale.value;
  return value.formatCurrency(locale);
});

const paymentSuffix = computed(() => {
  const frequency = parseInt(paymentFrequency.value);
  switch (frequency) {
    case 52:
      return t('views.project.calculator.weekly-suffix');
    case 26:
      return t('views.project.calculator.bi-weekly-suffix');
    case 12:
      return t('views.project.calculator.monthly-suffix');
    default:
      throw new Error(`Payment frequency '${paymentFrequency.value}' not implemented.`);
  }
});

watch(
  modelPrice,
  () => {
    if (modelPrice.value) {
      purchasePrice.value = modelPrice.value;
      downPayment.value = Math.round(purchasePrice.value * 0.2);
    }
  },
  { immediate: true }
);

watch(purchasePrice, (val) => {
  if (downPayment.value > val) {
    downPayment.value = val;
  }
});
</script>

<template>
  <section>
    <h2 class="2xs:text-lg xs:text-xl mb-2 text-base font-semibold">
      {{ t('views.project.calculator.header') }}
    </h2>
    <div class="mb-4 grid gap-4 rounded-md border-2 border-gray-200 bg-gray-50 p-4 pt-5">
      <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div class="relative">
          <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900">{{ t('views.project.calculator.purchase-price') }}</label>
          <input
            v-model.number="purchasePrice"
            class="focus:outline-ph-green-600 block w-full rounded-md border-none bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
            min="0"
            type="number"
          />
        </div>

        <div class="relative">
          <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900">{{ t('views.project.calculator.down-payment') }}</label>
          <input
            v-model.number="downPayment"
            :max="purchasePrice"
            class="focus:outline-ph-green-600 block w-full rounded-md border-none bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
            min="0"
            type="number"
          />
        </div>

        <div class="relative">
          <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900">{{ t('views.project.calculator.interest-rate') }}</label>
          <input
            v-model.number="interestRate"
            class="focus:outline-ph-green-600 block w-full rounded-md border-none bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
            min="0"
            step="0.01"
            type="number"
          />
        </div>

        <div class="relative">
          <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900">{{ t('views.project.calculator.amortization') }}</label>
          <select
            v-model="amortizationYears"
            class="focus:outline-ph-green-600 col-start-1 row-start-1 w-full appearance-none rounded-md border-none bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
          >
            <option value="5">{{ t('views.project.calculator.years', { count: 5 }) }}</option>
            <option value="10">{{ t('views.project.calculator.years', { count: 10 }) }}</option>
            <option value="15">{{ t('views.project.calculator.years', { count: 15 }) }}</option>
            <option value="20">{{ t('views.project.calculator.years', { count: 20 }) }}</option>
            <option value="25">{{ t('views.project.calculator.years', { count: 25 }) }}</option>
            <option value="30">{{ t('views.project.calculator.years', { count: 30 }) }}</option>
          </select>
        </div>

        <div class="relative">
          <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900">{{ t('views.project.calculator.term') }}</label>
          <select
            v-model="termYears"
            class="focus:outline-ph-green-600 col-start-1 row-start-1 w-full appearance-none rounded-md border-none bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
          >
            <option value="1">{{ t('views.project.calculator.years', { count: 1 }) }}</option>
            <option value="2">{{ t('views.project.calculator.years', { count: 2 }) }}</option>
            <option value="3">{{ t('views.project.calculator.years', { count: 3 }) }}</option>
            <option value="4">{{ t('views.project.calculator.years', { count: 4 }) }}</option>
            <option value="5">{{ t('views.project.calculator.years', { count: 5 }) }}</option>
            <option value="6">{{ t('views.project.calculator.years', { count: 6 }) }}</option>
            <option value="7">{{ t('views.project.calculator.years', { count: 7 }) }}</option>
            <option value="10">{{ t('views.project.calculator.years', { count: 10 }) }}</option>
          </select>
        </div>

        <div class="relative">
          <label class="absolute -top-2 left-2 inline-block rounded-lg bg-white px-1 text-xs font-medium text-gray-900">{{ t('views.project.calculator.payment-frequency') }}</label>
          <select
            v-model="paymentFrequency"
            class="focus:outline-ph-green-600 col-start-1 row-start-1 w-full appearance-none rounded-md border-none bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-2 outline-gray-300 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
          >
            <option value="52">{{ t('views.project.calculator.weekly') }}</option>
            <option value="26">{{ t('views.project.calculator.bi-weekly') }}</option>
            <option value="12">{{ t('views.project.calculator.monthly') }}</option>
          </select>
        </div>
      </div>

      <div class="mt-5 justify-center sm:grid sm:grid-cols-2 sm:gap-6">
        <div class="mb-6 overflow-hidden rounded-lg bg-white px-4 py-5 text-center shadow-sm sm:mb-0 sm:p-6">
          <div class="truncate text-sm font-medium text-gray-500">
            {{ t('views.project.calculator.mortgage-amount') }}
          </div>
          <div class="text-ph-green-600 mt-1 text-3xl font-bold tracking-tight">
            {{ mortgageAmount }}
          </div>
        </div>
        <div class="overflow-hidden rounded-lg bg-white px-4 py-5 text-center shadow-sm sm:p-6">
          <div class="truncate text-sm font-medium text-gray-500">
            {{ t('views.project.calculator.payments') }}
          </div>
          <div class="text-ph-green-600 mt-1 text-3xl font-bold tracking-tight">
            {{ paymentAmount }}
            <span class="text-ph-green-600/80 text-xl font-semibold">
              {{ paymentSuffix }}
            </span>
          </div>
        </div>
      </div>

      <div>
        <h6 class="mb-1 text-xs font-semibold tracking-tight text-gray-700">
          <button class="hover:text-ph-green-600 cursor-pointer" @click="showLegal = !showLegal">
            {{ t('views.project.calculator.legal-notice') }}
            <ChevronDownIcon v-if="showLegal" class="inline size-4" />
            <ChevronRightIcon v-else class="inline size-4" />
          </button>
        </h6>
        <p v-show="showLegal" class="text-xs text-gray-700">
          {{ t('views.project.calculator.legal') }}
        </p>
      </div>
    </div>
  </section>
</template>

<style scoped></style>
